import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';

interface PermissionGuardProps {
  children: React.ReactNode;
  requiredPermission?: string;
  requiredPermissions?: string[];
  requiredModulePermission?: { module: string; action: string };
  requiredSubModulePermission?: { module: string; subModule: string; action: string };
  fallback?: React.ReactNode;
  superAdminOnly?: boolean;
}

export function PermissionGuard({
  children,
  requiredPermission,
  requiredPermissions,
  requiredModulePermission,
  requiredSubModulePermission,
  fallback,
  superAdminOnly
}: PermissionGuardProps) {
  const { hasPermission, hasModulePermission, hasSubModulePermission, user } = useAuthStore();

  const checkPermissions = React.useCallback(() => {
    const normalizedRole = (user?.role || '').replace(/\s*\(Custom\)\s*$/i, '');
    const loweredRole = normalizedRole.toLowerCase();
    const isSuperAdmin = loweredRole === 'super admin' || loweredRole === 'super_admin';

    if (superAdminOnly) {
      return isSuperAdmin;
    }

    // Super Admin role has full access to everything (system role)
    if (isSuperAdmin) {
      return true;
    }

    // Check sub-module permission first
    if (requiredSubModulePermission) {
      const hasSubModulePerm = hasSubModulePermission(
        requiredSubModulePermission.module,
        requiredSubModulePermission.subModule,
        requiredSubModulePermission.action
      );
      return hasSubModulePerm;
    }

    // Check module permission
    if (requiredModulePermission) {
      const hasModulePerm = hasModulePermission(requiredModulePermission.module, requiredModulePermission.action);
      return hasModulePerm;
    }

    // Check single permission
    if (requiredPermission && !hasPermission(requiredPermission)) {
      return false;
    }

    // Check multiple permissions
    if (requiredPermissions && !requiredPermissions.some(permission => hasPermission(permission))) {
      return false;
    }

    return true;
  }, [requiredPermission, requiredPermissions, requiredModulePermission, requiredSubModulePermission, hasPermission, hasModulePermission, hasSubModulePermission, user, superAdminOnly]);

  if (!checkPermissions()) {
    if (fallback !== undefined) {
      return <>{fallback}</>;
    }
    // Redirect to unauthorized page
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}

// HOC to wrap components with permission check
export function withPermission<P extends object>(
  WrappedComponent: React.ComponentType<P>,
  requiredPermission?: string,
  requiredPermissions?: string[],
  requiredModulePermission?: { module: string; action: string }
) {
  return function WithPermissionComponent(props: P) {
    return (
      <PermissionGuard
        requiredPermission={requiredPermission}
        requiredPermissions={requiredPermissions}
        requiredModulePermission={requiredModulePermission}
      >
        <WrappedComponent {...props} />
      </PermissionGuard>
    );
  };
}

// Add default export
export default PermissionGuard; 
